import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Badge,
    Box,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    List,
    Popover,
    Tooltip,
    Typography,
} from '@mui/material';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import CakeRoundedIcon from '@mui/icons-material/CakeRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import confetti from 'canvas-confetti';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';
import { formatRelativeTime } from '../../utils/dateUtils';
import NotificationTypes from '../../constants/notificationTypes';
import '../../css/Notification.css';

const NotificationBell = () => {
    const { notifications, loading, markAsSeen, markAllAsSeen } = useNotifications();
    const [anchorEl, setAnchorEl] = useState(null);
    const bellRef = useRef(null);
    const confettiFired = useRef(false);
    const navigate = useNavigate();

    const unseenCount = notifications.length;
    const open = Boolean(anchorEl);

    const fireCelebrationConfetti = useCallback(() => {
        if (!bellRef.current) return;
        const rect = bellRef.current.getBoundingClientRect();
        const colors = ['#ff4081', '#f50057', '#7b1fa2', '#FFD700', '#FF69B4'];
        const originX = (rect.left + rect.width / 2) / window.innerWidth;
        const originY = (rect.top + rect.height / 2) / window.innerHeight;

        confetti({ particleCount: 50, angle: 60, spread: 260, origin: { x: originX - 0.05, y: originY }, colors });
        confetti({ particleCount: 50, angle: 120, spread: 260, origin: { x: originX + 0.05, y: originY }, colors });
    }, []);

    const handleOpen = useCallback((e) => {
        setAnchorEl(e.currentTarget);
        confettiFired.current = false;
    }, []);

    const handleClose = useCallback(() => {
        setAnchorEl(null);
        confettiFired.current = false;
    }, []);

    // Fire confetti when popover opens and there are celebration notifications
    useEffect(() => {
        if (open && !confettiFired.current) {
            const hasCelebrations = notifications.some(n => n.type === 'celebration');
            if (hasCelebrations) {
                confettiFired.current = true;
                setTimeout(() => fireCelebrationConfetti(), 120);
            }
        }
    }, [open, notifications, fireCelebrationConfetti]);

    const handleSeeAll = () => {
        handleClose();
        navigate('/home/notifications');
    };

    const handleMarkAllSeen = async () => {
        await markAllAsSeen();
    };

    /** Click on a notification row — mark seen and navigate if actionable */
    const handleNotifClick = useCallback((notif) => {
        markAsSeen(notif.id);
        if (notif.notificationType === NotificationTypes.LEAVE_APPLIED) {
            handleClose();
            navigate('/home/leave-approval');
        }
    }, [markAsSeen, handleClose, navigate]);

    return (
        <>
            <Tooltip title={unseenCount > 0 ? `${unseenCount} new notification${unseenCount > 1 ? 's' : ''}` : 'Notifications'}>
                <IconButton
                    ref={bellRef}
                    id="notification-bell-btn"
                    size="small"
                    onClick={handleOpen}
                    sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'rgba(15,23,42,0.06)' } }}
                >
                    <Badge
                        badgeContent={unseenCount}
                        max={99}
                        color="error"
                        overlap="circular"
                        sx={{
                            '& .MuiBadge-badge': {
                                fontSize: 10,
                                height: 16,
                                minWidth: 16,
                                fontWeight: 900,
                            },
                        }}
                    >
                        {unseenCount > 0
                            ? <NotificationsRoundedIcon fontSize="small" />
                            : <NotificationsNoneRoundedIcon fontSize="small" />}
                    </Badge>
                </IconButton>
            </Tooltip>

            <Popover
                id="notification-popover"
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: {
                        className: 'notif-popover-paper',
                        sx: { mt: 1, border: '1px solid', borderColor: 'divider' },
                    },
                }}
            >
                {/* Header */}
                <Box className="notif-popover-header">
                    <Box className="notif-popover-header-left">
                        <NotificationsRoundedIcon fontSize="small" color="primary" />
                        <Typography className="notif-popover-title">
                            Notifications
                        </Typography>
                        {unseenCount > 0 && (
                            <Chip
                                label={unseenCount}
                                size="small"
                                color="error"
                                sx={{ height: 18, fontSize: 11, fontWeight: 900, '.MuiChip-label': { px: 0.75 } }}
                            />
                        )}
                    </Box>
                    {unseenCount > 0 && (
                        <Tooltip title="Mark all as seen">
                            <IconButton
                                id="mark-all-seen-btn"
                                size="small"
                                onClick={handleMarkAllSeen}
                                sx={{ color: 'text.secondary' }}
                            >
                                <DoneAllRoundedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>

                {/* Body */}
                <Box className="notif-popover-body">
                    {loading ? (
                        <Box className="notif-loading">
                            <CircularProgress size={24} />
                        </Box>
                    ) : unseenCount === 0 ? (
                        <Box className="notif-empty">
                            <NotificationsNoneRoundedIcon className="notif-empty-icon" sx={{ color: 'text.disabled' }} />
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                                All caught up!
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                                No new notifications
                            </Typography>
                        </Box>
                    ) : (
                        <List disablePadding>
                            {notifications.map((notif, idx) => (
                                <React.Fragment key={notif.id}>
                                    <Box
                                        id={`notification-item-${notif.id}`}
                                        className="notif-item-row"
                                        onClick={() => handleNotifClick(notif)}
                                        sx={{
                                            cursor: notif.notificationType === NotificationTypes.LEAVE_APPLIED ? 'pointer' : 'default',
                                            '&:hover': notif.notificationType === NotificationTypes.LEAVE_APPLIED
                                                ? { bgcolor: 'rgba(4,88,100,0.05)' }
                                                : {},
                                        }}
                                    >
                                        {/* Icon circle */}
                                        <Box className={`notif-icon-circle notif-icon-circle--${notif.type}`}>
                                            {notif.notificationType === NotificationTypes.LEAVE_APPLIED
                                                ? <EventNoteRoundedIcon sx={{ fontSize: 15, color: '#045864' }} />
                                                : notif.type === 'celebration'
                                                    ? <CakeRoundedIcon sx={{ fontSize: 15, color: '#e91e63' }} />
                                                    : <WarningAmberRoundedIcon sx={{ fontSize: 15, color: '#ff9800' }} />
                                            }
                                        </Box>

                                        {/* Message */}
                                        <Box className="notif-message-area">
                                            <Typography className="notif-message-text">
                                                {notif.message}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.4 }}>
                                                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10, fontWeight: 600 }}>
                                                    {formatRelativeTime(notif.createdAt)}
                                                </Typography>
                                                {notif.notificationType === NotificationTypes.LEAVE_APPLIED && (
                                                    <Typography variant="caption" sx={{ color: 'var(--primary)', fontSize: 10, fontWeight: 700 }}>
                                                        Tap to review →
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>

                                        {/* Dismiss */}
                                        <Tooltip title="Mark as seen">
                                            <IconButton
                                                id={`dismiss-notif-${notif.id}`}
                                                size="small"
                                                onClick={(e) => { e.stopPropagation(); markAsSeen(notif.id); }}
                                                className="notif-dismiss-btn"
                                                sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                                            >
                                                <CloseRoundedIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    {idx < notifications.length - 1 && <Divider sx={{ mx: 2 }} />}
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </Box>

                {/* Footer */}
                <Divider />
                <Box
                    id="see-all-notifications-btn"
                    className="notif-popover-footer"
                    onClick={handleSeeAll}
                >
                    <EmojiEventsRoundedIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                    <Typography variant="caption" color="primary" className="notif-footer-link-text">
                        See all notifications
                    </Typography>
                    <ArrowForwardRoundedIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                </Box>
            </Popover>
        </>
    );
};

export default NotificationBell;
